/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useState, useEffect } from "react"
import { Drawer, Input, Button } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { isEmpty, replace } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import {
  ResponseWithError,
  CreateSectionRequest,
  ServiceHours,
} from "types/api"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"

import {
  mapSectionObj,
  mapServiceHours,
  ConfirmDeleteSectionOptions,
} from "utils/section_helper"
import { hasFormErrors, getValidationProps } from "utils/form_helper"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import { getUCMConfig } from "utils/config"

import { getDalErrMessage } from "utils/misc"
import { useCreateSectionMutation, useDeleteSectionMutation } from "queries"
import useConfirmedMutation from "queries/hooks/useConfirmedMutation"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  fetchCreateSection,
  refreshMenuAndCloseDrawers,
} from "utils/rtk_migration_helper"
import { SectionTimingsDrawer } from "./SectionTimingsDrawer"
import { ServiceHoursList } from "./ServiceHoursList"

import {
  drawerContentStyle,
  drawerBodyStyle,
  drawerFooterStyle,
} from "../styles"

interface SectionDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(e: any): void
}

export const SectionDrawerComponent: FC<SectionDrawerProps> = ({
  visible,
  onClose,
  form,
}: SectionDrawerProps) => {
  const dispatch = useDispatch()
  const {
    getFieldDecorator,
    getFieldValue,
    resetFields,
    isFieldsTouched,
  } = form

  const {
    flags: {
      createSection: isRtkCreateSectionEnabled,
      deleteSection: isRtkDeleteSectionEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [createSectionRtk] = useCreateSectionMutation()

  const { isEditAllowed } = useMenuEditorStatus()
  const {
    merchantID,
    drawer: { section },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const isEdit = !!section?.sectionID

  const [timingsFormData, setTimingsFormData] = useState<CreateSectionRequest>(
    {},
  )
  const [serviceHours, setServiceHours] = useState<ServiceHours>({})
  const [serviceHoursEdited, setServiceHoursEdited] = useState(false)
  const [showTimingDrawer, toggleShowTimingDrawer] = useState(false)

  const initializeFormData = () => {
    resetFields()
    const serviceHoursTmp = section?.openingHours ?? []
    setServiceHours(mapServiceHours(serviceHoursTmp))
    if (isEdit) {
      setTimingsFormData({
        sectionToEdit: {
          serviceHours: serviceHoursTmp,
        },
      })
    }
    setServiceHoursEdited(false)
  }

  useEffect(initializeFormData, [section, resetFields])

  const handleEditTimings = (data: CreateSectionRequest) => {
    setTimingsFormData(data)
    setServiceHoursEdited(true)
    setServiceHours(
      (isEdit
        ? data.sectionToEdit?.serviceHours
        : data.sectionToCreate?.serviceHours) ?? {},
    )
  }

  const textMap = useTranslationsMap({
    deleteSection: "menu.drawers.section.buttons.deleteSection",
    createSuccess: "menu.notifications.createSection.successMessage",
    updateSuccess: "menu.notifications.updateSection.successMessage",
    deleteSuccess: "menu.notifications.deleteSection.successMessage",
    errorRequired: "menu.errors.requiredField",
    errorCharacterLimit: "menu.errors.characterLimit",
    errorExceedMaxSectionLimit: "menu.errors.section.exceedMaxSectionLimit",
  })

  const errorCallback = (response: ResponseWithError) => {
    const { reason = "", message = "", target = "" } = response ?? {}

    if (reason === "invalid_argument") {
      if (target === "SectionServiceHours") {
        // message is already localized on BE
        return message
      }

      const dalErrMsg = getDalErrMessage(message)
      if (dalErrMsg) {
        return dalErrMsg
      }
    }

    if (reason === "forbidden") {
      if (message.includes("cannot create more")) {
        return textMap.errorExceedMaxSectionLimit
      }
    }

    return true
  }

  const handleSave = async () => {
    form.validateFields(async (err, formValues) => {
      if (err) return

      let sectionData = {
        merchantID,
        serviceHours,
        ...formValues,
      }

      const sectionsToUpdate = timingsFormData?.sectionsToUpdate

      const data = {
        sectionsToUpdate,
      } as CreateSectionRequest

      let resp
      if (isEdit && sectionData && section) {
        const sectionObj = mapSectionObj(section)

        sectionData = {
          ...sectionObj,
          ...sectionData,
        }

        const sections = [
          sectionData,
          ...(data?.sectionsToUpdate ?? []),
        ].map(s => ({ ...s, merchantID, menuGroupID }))

        resp = await dispatch(
          actionCreator.menu.updateSections({
            merchantID,
            menuGroupID,
            merchantGroupID,
            data: {
              sections,
            },
            successMessage: textMap.updateSuccess,
            errorCallback,
          }),
        )
      } else {
        // While migration flags are loading, do not fall back to old Redux: the enabled bit is
        // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
        // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
        // the trigger UI while loading so clicks are not silently dropped.
        if (isRtkFlagLoading) return

        data.sectionToCreate = sectionData

        const createSectionResult = await fetchCreateSection({
          dispatch,
          merchantID,
          merchantGroupID,
          menuGroupID,
          data,
          successMessage: textMap.createSuccess,
          errorCallback,
          getErrorNotificationContent: ({
            data: errorData,
            defaultMessage,
          }) => {
            const callbackValue = errorCallback(errorData as ResponseWithError)

            return {
              message:
                typeof callbackValue === "string"
                  ? callbackValue
                  : defaultMessage,
            }
          },
          isRtkCreateSectionEnabled,
          createSectionRtk,
        })

        if (!createSectionResult) return

        let labels = {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          SECTION_ID: section?.sectionID,
          INTENT: "ADD_SECTION",
          STATUS: "SUCCESS",
          REASON: "",
          ERROR_MSG: "",
        }

        if (
          createSectionResult.fromRtkQuery &&
          createSectionResult.result.error
        ) {
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabelsForRTK(createSectionResult.result),
          }
        } else if (
          !createSectionResult.fromRtkQuery &&
          createSectionResult.result?.type.endsWith("_FAIL")
        ) {
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabels(createSectionResult.result),
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewModifySection,
          action: EventAction.submitModifySection,
          labels,
        })
      }

      if (resp && isEdit) {
        let labels = {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          SECTION_ID: section?.sectionID,
          INTENT: "EDIT_SECTION",
          STATUS: "SUCCESS",
          REASON: "",
          ERROR_MSG: "",
        }

        if (resp?.type.endsWith("_FAIL")) {
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewModifySection,
          action: EventAction.submitModifySection,
          labels,
        })
      }
    })
  }

  const confirmDeleteSectionOptions = section
    ? ConfirmDeleteSectionOptions(section)
    : {}

  const [deleteSectionRtk] = useConfirmedMutation(
    useDeleteSectionMutation,
    confirmDeleteSectionOptions,
  )

  const handleDelete = async () => {
    if (!section?.sectionID) return

    const sendMigrationEvent = (fromRtkQuery: boolean) => {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.deleteSection,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkDeleteSectionEnabled,
          FROM_RTK_QUERY: fromRtkQuery,
          FROM_OLD_REDUX: !fromRtkQuery,
          ACTION_NAME: "deleteSection",
        },
      })
    }

    if (!isRtkFlagLoading && isRtkDeleteSectionEnabled) {
      const result = await deleteSectionRtk({
        merchantID,
        merchantGroupID,
        sectionID: section.sectionID,
        menuGroupID,
        showSuccess: true,
        successOptions: { message: textMap.deleteSuccess },
      })

      sendMigrationEvent(true)
      if (result === null) return

      let labels = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section.sectionID,
        STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }

      if ("error" in result) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabelsForRTK(result),
        }
      } else {
        refreshMenuAndCloseDrawers({
          dispatch,
          merchantID,
          merchantGroupID,
          menuGroupID,
          isMartDepartmentStructure,
          isSellingTimeStructure,
          enablePartialMenu,
        })
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewModifySection,
        action: EventAction.submitDeleteSection,
        labels,
      })
      // While migration flags are loading, do not fall back to old Redux: the enabled bit is
      // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
      // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
      // the trigger UI while loading so clicks are not silently dropped.
    } else if (!isRtkFlagLoading) {
      const resp: any = await dispatch(
        actionCreator.menu.deleteSection({
          merchantID,
          sectionID: section.sectionID,
          section,
          confirmOptions: confirmDeleteSectionOptions,
          successMessage: textMap.deleteSuccess,
          menuGroupID,
          merchantGroupID,
        }),
      )

      sendMigrationEvent(false)
      if (resp) {
        let labels = {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          SECTION_ID: section.sectionID,
          STATUS: "SUCCESS",
          REASON: "",
          ERROR_MSG: "",
        }

        if (resp?.type.endsWith("_FAIL")) {
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewModifySection,
          action: EventAction.submitDeleteSection,
          labels,
        })
      }
    }
  }

  // Only show error after a field is touched.
  const validationProps = getValidationProps(
    form,
    ["name"],
    isEditAllowed && isEdit,
  )

  return (
    <Fragment>
      <Drawer
        destroyOnClose
        data-testid="drawer"
        title={useTranslation(
          isEdit
            ? "menu.drawers.section.editSection"
            : "menu.drawers.section.addNewSection",
        )}
        width={450}
        open={visible}
        onClose={e => {
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewModifySection,
            action: EventAction.clickExit,
            labels: {
              ...(merchantID
                ? { STORE_ID: merchantID }
                : { MENU_GROUP_ID: menuGroupID }),
              SECTION_ID: section?.sectionID,
              INTENT: isEdit ? "EDIT_SECTION" : "ADD_SECTION",
            },
          })
          onClose(e)
        }}
      >
        <div css={drawerBodyStyle}>
          <div
            css={css`
              flex: 1 0 auto;
            `}
          >
            <div css={drawerContentStyle}>
              <Form.Item
                label={useTranslation("menu.drawers.section.labels.name")}
                {...validationProps.name}
              >
                {// @ts-ignore
                getFieldDecorator("name", {
                  initialValue: section?.sectionName,
                  rules: [
                    {
                      required: true,
                      message: textMap.errorRequired,
                    },
                    {
                      max: getUCMConfig("maxLengthSectionName"),
                      message: replace(
                        textMap.errorCharacterLimit,
                        "$limit",
                        getUCMConfig("maxLengthSectionName"),
                      ),
                    },
                  ],
                })(
                  <Input
                    data-testid="input-name"
                    autoComplete="off"
                    placeholder={useTranslation(
                      "menu.drawers.section.placeholders.name",
                    )}
                    disabled={!isEditAllowed}
                  />,
                )}
              </Form.Item>
            </div>
            {!isMartDepartmentStructure && (
              <ServiceHoursList
                data-testid="service-hours-list"
                serviceHours={serviceHours}
                disabledEdit={!isEditAllowed || !getFieldValue("name")}
                onClickEditTimings={() => {
                  TroyWebTracker.sendEvent({
                    category: EventCategory.menuOverviewTab,
                    action: EventAction.clickEditTiming,
                    labels: {
                      ...(merchantID
                        ? { STORE_ID: merchantID }
                        : {
                            MERCHANT_GROUP_ID: merchantGroupID,
                            MENU_GROUP_ID: menuGroupID,
                          }),
                      SECTION_ID: section?.sectionID,
                      INTENT: isEdit ? "EDIT_SECTION" : "ADD_SECTION",
                    },
                  })
                  toggleShowTimingDrawer(true)
                }}
              />
            )}
            {isEdit && !isMartDepartmentStructure && (
              <div
                css={css`
                  padding: 24px;
                `}
              >
                <Button
                  data-testid="delete-button"
                  danger
                  block
                  onClick={handleDelete}
                  disabled={!isEditAllowed || isRtkFlagLoading}
                >
                  {textMap.deleteSection}
                </Button>
              </div>
            )}
          </div>
          <div css={drawerFooterStyle}>
            <Button
              data-testid="action-button"
              type="primary"
              block
              onClick={handleSave}
              disabled={
                !isEditAllowed ||
                (!isFieldsTouched() && !serviceHoursEdited) ||
                hasFormErrors(form) ||
                isEmpty(serviceHours) ||
                (!isEdit && isRtkFlagLoading)
              }
            >
              {useTranslation(
                isEdit
                  ? "menu.buttons.saveChanges"
                  : "menu.drawers.section.buttons.addSection",
              )}
            </Button>
          </div>
        </div>
      </Drawer>
      <SectionTimingsDrawer
        data-testid="section-timing-drawer"
        section={{
          ...(section ?? {}),
          sectionName: getFieldValue("name"),
        }}
        isEdit={isEdit}
        onClose={() => toggleShowTimingDrawer(false)}
        onSave={handleEditTimings}
        visible={showTimingDrawer}
      />
    </Fragment>
  )
}

export const SectionDrawer = Form.create<SectionDrawerProps>({})(
  SectionDrawerComponent,
)
